import { Component, Emit, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import PatientSearcherComponent from '@/components/common/searcher/components/patient/patient-searcher.vue'
import PatientSearcher from '@/components/common/searcher/components/patient/patient-searcher'
import CustomIdentifierSearcherComponent from '@/components/common/searcher/components/custom-identifier/custom-identifier-searcher.vue'
import CustomIdentifierSearcher from '@/components/common/searcher/components/custom-identifier/custom-identifier-searcher'
import BillingModalPatientSelectionComponent from '@/modules/billing-module/components/modal/patient/selection/billing-modal-patient-selection.vue'
import BillingModalPatientSelection from '@/modules/billing-module/components/modal/patient/selection/billing-modal-patient-selection'
import {
    BillingSearcherCaseModeInfo,
    BillingSearcherMode,
    BillingSearcherModeInfo,
    BillingSearcherParams,
    BillingSearcherState,
    caseScopes,
    getSearcherModeInfo,
    initialBillingSearcherState
} from '@/modules/billing-module/components/searcher/billing-searcher.model'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'
import { BillingGridDatasource } from '@/modules/billing-module/components/grid/config/datasources/billing-grid.datasource'
import { BillingGridResponse } from '@/modules/billing-module/components/grid/config/datasources/billing-grid-datasource-factory'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { SearcherStatus } from '@/components/common/searcher/shared/models/common-searcher.model'
import { PatientSearchRequest, PatientSearchResponse } from '@/api/services/patient/patient/patient.dto'
import { PatientSearcherRequestParams } from '@/components/common/searcher/components/patient/patient-searcher.model'
import patientResource from '@/api/resources/PatientResource'
import { CustomIdentifierSearcherRequestParams } from '@/components/common/searcher/components/custom-identifier/custom-identifier-searcher.model'
import { store } from '@/store/store'

@Component({
    components: {
        PatientSearcherComponent,
        CustomIdentifierSearcherComponent,
        BillingModalPatientSelectionComponent
    }
})
export default class BillingSearcher extends Vue {
    @Prop({ type: Object, required: true }) readonly datasource!: BillingGridDatasource<BillingGridResponse>

    @Ref() patientSearcher!: PatientSearcher<PatientSearchResponse>
    @Ref() caseSearcher!: CustomIdentifierSearcher<BillingSearcherCaseModeInfo, string>
    @Ref() identifierSearcher!: CustomIdentifierSearcher<string, string>

    @Ref() patientSelectionModal!: BillingModalPatientSelection

    @Emit()
    searchSuccess(billingGridResponse: BillingGridResponse[]): BillingGridResponse[] {
        return billingGridResponse
    }

    @Watch('datasource')
    onDatasourceChanged(): void {
        if (this.state.searcherMode) {
            const modes = this.searcherModes.map(searcherMode => searcherMode.mode)
            if (!modes.includes(this.state.searcherMode.mode)) {
                this._initSearcher()
            }
        }
    }

    state: BillingSearcherState
    constructor() {
        super()
        this.state = initialBillingSearcherState()
    }

    get searcherModes(): BillingSearcherModeInfo[] {
        const searcherModes = []
        for (const searcherMode of this.datasource.getSearcherModes()) {
            const info = getSearcherModeInfo(searcherMode)
            if (info) {
                searcherModes.push(info)
            }
        }
        return searcherModes
    }

    get visiblePatientSearcher(): boolean {
        return this.state.searcherMode?.mode === BillingSearcherMode.PATIENT
    }
    get visibleCaseSearcher(): boolean {
        return this.state.searcherMode?.mode === BillingSearcherMode.CASE
    }
    get visibleIdentifierSearcher(): boolean {
        return this.state.searcherMode?.mode === BillingSearcherMode.CUSTOMER ||
            this.state.searcherMode?.mode === BillingSearcherMode.DELIVERY_NOTE ||
            this.state.searcherMode?.mode === BillingSearcherMode.INVOICE ||
            this.state.searcherMode?.mode === BillingSearcherMode.INVOICE_PAYMENT ||
            this.state.searcherMode?.mode === BillingSearcherMode.INVOICE_RECEIPT
    }

    get initialCaseIdentifier(): BillingSearcherCaseModeInfo {
        return caseScopes()[0]
    }

    private _initSearcher(): void {
        this.state.searcherMode = this.searcherModes[0]
    }

    /// VUE HOOKS ///

    created(): void {
        this._initSearcher()
    }
    mounted(): void {
        this.focus()
    }

    ///

    focus(): void {
        if (this.patientSearcher) {
            this.patientSearcher.focus()
        } else if (this.caseSearcher) {
            this.caseSearcher.focus()
        } else if (this.identifierSearcher) {
            this.identifierSearcher.focus()
        }
    }

    onSearcherModeChanged(): void {
        this.focus()
    }

    onSearchStatusChanged(status: SearcherStatus): void {
        switch (status) {
        case SearcherStatus.ERROR:
            ModalUtils.showErrorModal(this.$tc('component.modalError.title'))
            break
        case SearcherStatus.NO_RESULTS:
            if (this.state.searcherMode?.mode === BillingSearcherMode.PATIENT) {
                ModalUtils.showErrorModal(this.$tc('accounting.searcher.patient.noResults'))
            } else {
                ModalUtils.showErrorModal(this.$tc('global.noResults'))
            }
            break
        case SearcherStatus.INVALID_IDENTIFIER:
            ModalUtils.showErrorModal(this.$tc('component.searcher.error.validIdentifier'))
            break
        case SearcherStatus.TOO_MANY_RESULTS:
            ModalUtils.showErrorModal(this.$tc('component.patient.searcher.tooManySearchResults'))
            break
        }
    }

    /// PATIENT SEARCH ///

    async searcherByPatient(searcherParams: PatientSearcherRequestParams): Promise<PatientSearchResponse[]> {
        const { identifierType, center, id, name, middleName, surname1, surname2 } = searcherParams
        const params: PatientSearchRequest = {
            requestingLaboratoryId: store.state.labId,
            id,
            name,
            middleName,
            surname1,
            surname2,
            identifierType: identifierType?.code
        }
        if (identifierType?.idtScope === IdentifierTypeScope.CENTER_PATIENT) {
            params.centerCode = center?.code
        }
        const response = await patientResource.paginator(params)
        return response?.content ?? []
    }

    private async _searchPatient(patients: PatientSearchResponse[]): Promise<void> {
        if (this.datasource && this.state.searcherMode && patients.length) {
            const params: BillingSearcherParams = {
                mode: this.state.searcherMode.mode,
                patientsIds: patients.map(patient => patient.patId)
            }
            const response = await PromiseUtils.toCallSecure(this.datasource.searcher(params))
            if (!response?.content.length) {
                ModalUtils.showErrorModal(this.$tc('accounting.searcher.patient.cases.noResults'))
            } else {
                this.searchSuccess(response.content ?? [])
            }
        }
    }
    async onPatientSearched(patients: PatientSearchResponse[]): Promise<void> {
        if (patients.length) {
            if (patients.length === 1) {
                await this._searchPatient(patients)
            } else {
                this.patientSelectionModal.show(patients)
            }
        }
    }
    async onPatientsSelected(patients: PatientSearchResponse[]): Promise<void> {
        await this._searchPatient(patients)
        this.patientSelectionModal.hide()
    }

    /// CASE SEARCH ///

    getCaseIdentifiers(): BillingSearcherCaseModeInfo[] {
        return caseScopes()
    }

    async searcherByCase(searcherParams: CustomIdentifierSearcherRequestParams<BillingSearcherCaseModeInfo>): Promise<string[]> {
        const { customIdentifier, id } = searcherParams
        this.state.caseMode = customIdentifier?.mode ?? null
        return [id]
    }

    async onCaseSearched(caseIds: string[]): Promise<void> {
        if (this.datasource && this.state.searcherMode && this.state.caseMode && caseIds.length) {
            const params: BillingSearcherParams = {
                mode: this.state.searcherMode.mode,
                caseScope: this.state.caseMode,
                identifierSearch: caseIds[0]
            }
            const response = await PromiseUtils.toCallSecure(this.datasource.searcher(params))
            if (!response?.content.length) {
                ModalUtils.showErrorModal(this.$tc('accounting.searcher.case.identifier.noResults'))
            } else {
                this.searchSuccess(response.content ?? [])
            }
        }
    }

    /// IDENTIFIER SEARCH: customer / delivery note / invoice ///

    async searcherByIdentifier(searcherParams: CustomIdentifierSearcherRequestParams<string>): Promise<string[]> {
        return [searcherParams.id]
    }

    async onIdentifierSearched(ids: string[]): Promise<void> {
        if (this.datasource && this.state.searcherMode && ids.length) {
            const params: BillingSearcherParams = {
                mode: this.state.searcherMode.mode,
                identifierSearch: ids[0]
            }
            const response = await PromiseUtils.toCallSecure(this.datasource.searcher(params))
            if (!response?.content.length) {
                switch (this.state.searcherMode.mode) {
                case BillingSearcherMode.CUSTOMER:
                    ModalUtils.showErrorModal(this.$tc('accounting.searcher.customer.noResults'))
                    break
                case BillingSearcherMode.DELIVERY_NOTE:
                    ModalUtils.showErrorModal(this.$tc('accounting.searcher.deliveryNotes.noResults'))
                    break
                case BillingSearcherMode.INVOICE:
                    ModalUtils.showErrorModal(this.$tc('accounting.searcher.invoices.noResults'))
                    break
                case BillingSearcherMode.INVOICE_PAYMENT:
                    ModalUtils.showErrorModal(this.$tc('accounting.searcher.invoicePayments.noResults'))
                    break
                case BillingSearcherMode.INVOICE_RECEIPT:
                    ModalUtils.showErrorModal(this.$tc('accounting.searcher.invoiceReceipts.noResults'))
                    break
                }
            } else {
                this.searchSuccess(response.content ?? [])
            }
        }
    }
}
